import { assert, type IsExact } from 'conditional-type-checks';

import type { DeviceViewAnchorConfig } from '../oas/OasOperationObjectExtension';
import type { TableColumnBase } from './columns/TableColumnBase';
import type { TableColumnFormatAsn } from './columns/types/asnColumn';
import type { TableColumnFormatBgpRouting } from './columns/types/bgpRoutingColumn';
import type { TableColumnFormatBoolean } from './columns/types/booleanColumn';
import type { TableColumnFormatCompactNumber } from './columns/types/compactNumberColumn';
import type { TableColumnFormatDateTime } from './columns/types/dateTimeColumn';
import type { TableColumnFormatEnum } from './columns/types/enumColumn';
import type { TableColumnFormatFrontendOnly } from './columns/types/frontendOnlyColumn';
import type { TableColumnFormatHostname } from './columns/types/hostnameColumn';
import type { TableColumnFormatHostnameObject } from './columns/types/hostnameObjectColumn';
import type { TableColumnFormatInterface } from './columns/types/interfaceColumn';
import type { TableColumnFormatLabels } from './columns/types/labelsColumn';
import type { TableColumnFormatMplsNextHop } from './columns/types/mplsNextHopColumn';
import type { TableColumnFormatNeighborIp } from './columns/types/neighborIpColumn';
import type { TableColumnFormatNtpSummarySources } from './columns/types/ntpSummarySourcesColumn';
import type { TableColumnFormatNumber } from './columns/types/numberColumn';
import type { TableColumnFormatObject } from './columns/types/objectColumn';
import type { TableColumnFormatSecurity } from './columns/types/securityColumn';
import type { TableColumnFormatSwitchportIntName } from './columns/types/switchportIntNameColumn';
import type { TableColumnFormatText } from './columns/types/textColumn';
import type { TableColumnFormatUptime } from './columns/types/uptimeColumn';
import type { TableColumnFormatUrl } from './columns/types/urlColumn';

export type TableColumnFormat<TRow> =
  | TableColumnFormatFrontendOnly
  | TableColumnFormatHostname<TRow>
  | TableColumnFormatNumber<TRow>
  | TableColumnFormatText<TRow>
  | TableColumnFormatBoolean<TRow>
  | TableColumnFormatUptime<TRow>
  | TableColumnFormatDateTime<TRow>
  | TableColumnFormatCompactNumber<TRow>
  | TableColumnFormatAsn<TRow>
  | TableColumnFormatUrl<TRow>
  // Consider removing TableColumnFormatSecurity type in the future and
  // refactoring into a `link` variant like `cloudSecurityTree`.
  | TableColumnFormatSecurity<TRow>
  | TableColumnFormatMplsNextHop<TRow>
  | TableColumnFormatBgpRouting<TRow>
  | TableColumnFormatSwitchportIntName<TRow>
  | TableColumnFormatNeighborIp<TRow>
  | TableColumnFormatInterface<TRow>
  | TableColumnFormatLabels<TRow>
  // Any since TS is not able to infer correct type from unknown.
  // Typing of table endpoint definition still works with this so it's not a big deal
  | TableColumnFormatObject<TRow, any>
  | TableColumnFormatEnum<TRow, any>
  | TableColumnFormatNtpSummarySources<TRow, any>
  | TableColumnFormatHostnameObject<TRow, any>;

const tableColumnFormatTypes = [
  'frontendOnly',
  'hostname',
  'number',
  'text',
  'boolean',
  'uptime',
  'dateTime',
  'compactNumber',
  'asn',
  'url',
  'security',
  'mplsNextHop',
  'bgpRouting',
  'switchportIntName',
  'neighborIp',
  'object',
  'enum',
  'ntpSummarySources',
  'hostnameObject',
  'interface',
  'labels',
  // just satisfy the type but infer actual type to make the type check below work
] satisfies TableColumnFormat<never>['type'][];

// if this starts to fail, tableColumnFormatTypes is out of sync with actual list of column types

assert<
  IsExact<typeof tableColumnFormatTypes, TableColumnFormat<never>['type'][]>
>(true);

export const isBackendColumnFormat = (
  format: TableColumnFormat<any>,
): boolean => format.type !== 'frontendOnly';

export type TableSnapshotArgumentMode = 'required' | 'optional' | 'forbidden';

export type TableColumn<TRow> = TableColumnBase<TRow, TableColumnFormat<TRow>>;

export type TableEndpoint<TRow> = {
  /**
   * Unique identifier of table endpoint, also used as default id of table settings !! SEE IMPORTANT NOTE BELOW !!
   *
   * We can't use neither apiEndpoint because there can be multiple table definitions using the same api endpoint,
   * nor webEndpoint in all cases, because even table used in some modal without canonical page can have settings;
   * although webEndpoint will be used in most cases - see below.
   *
   * !!!!!!!!!!!!!
   * IMPORTANT for migration current tables to new Table: this should preserve name of the table,
   * which is currently determined as (name prop ?? (webEndpoint+nameSuffix) ?? webEndpoint (= URL path on frontend))
   * to preserve settings and especially intent checks saved for the table. For example:
   * - name prop: Used in multiple tables on page to prevent overriding data in redux; will likely not be needed
   *   by new Table since instances don't share Redux state, so we should be able to discard names there:
   *   - components/DeviceView/DeviceViewContent/DeviceViewDetailTable.tsx
   *   - pages/SnapshotManagement/Tabs/SnapshotInventory.tsx
   *   - components/Graph/GraphSnapshotCreation/GraphSnapshotCreation.tsx
   * - two tables in Settings > Authentication and several others have nameSuffix prop (easy to grep)
   * - all others use url, e.g. `/tables/inventory/devices` has name generated from web url `/inventory/devices`.
   */
  id: string;
  apiEndpoint: string;
  /**
   * Canonical path of the table on frontend
   *
   * Must be a path where this is the only / main table on the page, therefore able to consume data from URL etc.
   * I not present, the table has no such page, so it is not possible to create direct links to it etc.
   */
  webPath?: string;
  /**
   * Whether or not snapshot ID is allowed to be sent in request
   */
  snapshot?: TableSnapshotArgumentMode;
  requireAttributeFilters: boolean;
  /**
   * Used as table title in UI and to generate exported file name
   */
  title: string;
  /** Markdown description of table content */
  description?: string;
  /** OAS operation id for table endpoint */
  operationId?: string;
  /** Additional columns to be fetched on web */
  webDependencies?: string[];
  deviceViewAnchor?: DeviceViewAnchorConfig;

  /** Indicator for device specific filter options in filters and intent checks */
  deviceFilters: {
    enabled: boolean;
    properties?: string[];
  };

  columns: TableColumn<TRow>[];
  deprecated?: boolean;
};
